import cn from "classnames";
import { useBoolean } from "hooks";
import { t } from "i18next";
import { ForwardedRef, forwardRef } from "react";
import { FormField } from "ui/FormField";

import { InputProps } from "./Input.types";

import styles from "./Input.module.scss";

const Input = forwardRef(
  (
    {
      label,
      errorMessage,
      asTag: Tag = "input",
      isPassword = false,
      action = null,
      maxLength = Infinity,
      preventConsecutiveSpaces = false,
      onlyInteger = false,
      errorWithoutMessage = false,
      onChange,
      type,
      className,
      containerClassName,
      ...props
    }: InputProps,
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const { value: showPassword, toggle: toggleShowPassword } =
      useBoolean(false);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;

      if (!onChange || newValue.length > maxLength) {
        return;
      }

      if (newValue === "") {
        onChange(event);
        return;
      }

      if (
        preventConsecutiveSpaces &&
        (newValue.startsWith(" ") || / {2,}/.test(newValue))
      ) {
        return;
      }

      if (type === "number") {
        const hasOnlyDigits = /^[0-9-]+$/.test(newValue);
        const hasOnlyDigitsAndPoint = /^[0-9.-]+$/.test(newValue);

        if ((onlyInteger && !hasOnlyDigits) || !hasOnlyDigitsAndPoint) {
          return;
        }

        if (newValue.startsWith(".")) {
          return;
        }

        if (newValue.endsWith(".") && newValue.slice(0, -1).includes(".")) {
          return;
        }

        if (newValue.slice(1).includes("-")) {
          return;
        }

        if (newValue === "00") {
          return;
        }
      }

      onChange(event);
    };

    return (
      <FormField
        label={label}
        errorMessage={errorMessage}
        containerClassName={containerClassName}
      >
        <div className={styles.input__wrapper}>
          <Tag
            ref={ref}
            className={cn(
              styles.input,
              {
                [styles.input__error]: !!errorMessage || !!errorWithoutMessage
              },
              { [styles.input__password]: isPassword },
              className
            )}
            type={
              isPassword
                ? showPassword
                  ? "text"
                  : "password"
                : type === "number"
                  ? "text"
                  : type
            }
            onChange={handleInputChange}
            {...props}
            placeholder={props.placeholder ? t(props.placeholder) : undefined}
          />

          {isPassword ? (
            <button
              className={cn("reset-button", styles["input__password--button"])}
              type="button"
              onClick={toggleShowPassword}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          ) : null}
          {action}
        </div>
      </FormField>
    );
  }
);

Input.displayName = "Input";

export default Input;
